/**
 * AuraPanel - Persistent UI component with hints and save/share
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, BookmarkIcon, ShareIcon } from '@heroicons/react/24/outline';

const AuraPanel = ({ turn, session, showReflection }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hints = [
    'Every signal has multiple perspectives. Listen to all voices before deciding.',
    'Not all signals require immediate action. Sometimes waiting is wise.',
    'Weigh the tradeoffs: what you gain, and what you risk.',
    'Your meters show your financial position. Keep them balanced.',
    'Signal vs noise: focus on what matters to your goals.'
  ];

  return (
    <motion.div
      className="fixed bottom-4 right-4 z-50"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="border border-hairline bg-bone-raised rounded-lg">
        {!isExpanded && (
          <button
            type="button"
            onClick={() => setIsExpanded(true)}
            className="p-4 flex items-center gap-3 text-primary hover:bg-pine-tint rounded-lg transition-colors duration-300"
          >
            <SparklesIcon className="w-6 h-6 text-pine" />
            <span className="text-label font-medium">Aura</span>
          </button>
        )}

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-80 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="w-6 h-6 text-pine" />
                  <span className="font-display text-h3 text-primary">Aura</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  className="btn-ghost p-1 text-secondary"
                  aria-label="Close panel"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="border border-hairline rounded-md p-3 bg-bone text-label text-secondary">
                  <div className="font-medium text-primary mb-2">Hint</div>
                  <div>{hints[turn?.turn_number % hints.length || 0]}</div>
                </div>

                <div className="border border-hairline rounded-md p-3 bg-bone text-label text-secondary">
                  <div className="font-medium text-primary mb-2">Signal vs noise</div>
                  <div>
                    This signal comes from the {turn?.district?.replace('_', ' ')} district. Tie
                    this signal to your goals before you act.
                  </div>
                </div>

                {showReflection && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      className="btn-secondary w-full py-2 text-label flex items-center justify-center gap-2"
                    >
                      <BookmarkIcon className="w-5 h-5" />
                      Save Insight
                    </button>
                    <button
                      type="button"
                      className="btn-secondary w-full py-2 text-label flex items-center justify-center gap-2"
                    >
                      <ShareIcon className="w-5 h-5" />
                      Share to Community
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default AuraPanel;
